import { useMemo } from 'react';
import clsx from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export interface PaginationProps {
  page: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  className?: string;
}

/**
 * Build the list of page numbers to display, inserting null for ellipsis gaps.
 * Always shows the first page, last page, and pages near the current page.
 */
function buildPageRange(current: number, totalPages: number): (number | null)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | null)[] = [];

  // Always show first page
  pages.push(1);

  if (current > 3) {
    pages.push(null); // ellipsis
  }

  // Pages around current
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < totalPages - 2) {
    pages.push(null); // ellipsis
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

export const Pagination = ({
  page,
  total,
  pageSize,
  onPageChange,
  className,
}: PaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  const pages = useMemo(() => buildPageRange(page, totalPages), [page, totalPages]);

  return (
    <div
      className={clsx(
        'flex items-center justify-between px-5 py-3 border-t border-[var(--color-border-light)]',
        className
      )}
    >
      {/* Info text */}
      <span className="text-sm text-[var(--color-text-secondary)]">
        Showing{' '}
        <span className="font-mono font-medium text-[var(--color-text-primary)]">
          {startItem}
        </span>{' '}
        to{' '}
        <span className="font-mono font-medium text-[var(--color-text-primary)]">
          {endItem}
        </span>{' '}
        of{' '}
        <span className="font-mono font-medium text-[var(--color-text-primary)]">
          {total.toLocaleString()}
        </span>
      </span>

      {/* Page controls */}
      <div className="flex items-center gap-1">
        {/* Previous */}
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="btn-ghost p-2 disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>

        {/* Page numbers */}
        {pages.map((p, idx) =>
          p === null ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-1 text-sm text-[var(--color-text-muted)]"
            >
              ...
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={clsx(
                'min-w-[32px] rounded-lg px-2 py-1 text-sm font-medium transition-colors',
                p === page
                  ? 'bg-primary-500 text-white'
                  : 'text-[var(--color-text-secondary)] hover:bg-surface-sunken'
              )}
            >
              {p}
            </button>
          )
        )}

        {/* Next */}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="btn-ghost p-2 disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
